/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

/**
 * Ported from @material-ui/core@4.11.3
 */

import { type FocusEventHandler, type MouseEventHandler } from 'react';
import cx from 'classnames';
import { ButtonBase } from '../ButtonBase/ButtonBase';
import styles from './Tab.module.scss';

export type TabProps<T> = {
  /**
   * @ignore
   */
  className?: string;

  /**
   * If `true`, the tab will be disabled.
   */
  disabled?: boolean;

  /**
   * @ignore
   */
  fullWidth?: boolean;

  /**
   * The icon element.
   */
  icon?: React.ReactNode;

  /**
   * @ignore
   * For server-side rendering consideration, we let the selected tab
   * render the indicator.
   */
  indicator?: React.ReactNode;

  /**
   * The label element.
   */
  label?: React.ReactNode;

  /**
   * @ignore
   */
  onChange?: (event: React.SyntheticEvent<HTMLElement>, activeTabIndex?: T) => void;

  /**
   * @ignore
   */
  onClick?: (event: React.MouseEvent<HTMLElement>) => unknown;

  /**
   * @ignore
   */
  onFocus?: (event: React.FocusEvent<HTMLElement>) => unknown;

  /**
   * @ignore
   */
  selected?: boolean;

  /**
   * @ignore
   */
  selectionFollowsFocus?: boolean;

  /**
   * You can provide your own value. Otherwise, we fallback to the child position index.
   */
  value?: T;
  classes?: {
    [key: string]: string;
  };
  style?: {
    [key: string]: number | string;
  };
  tabIndex?: number;

  /**
   * Specifies the ID of the associated tabpanel for accessibility
   */
  'aria-controls'?: string;

  /**
   * Unique identifier for the tab
   */
  id?: string;
};

const Tab = function Tab<T>(props: TabProps<T>) {
  const {
    className,
    classes = {},
    disabled = false,
    fullWidth,
    icon,
    indicator,
    label,
    onChange,
    onClick,
    onFocus,
    selected,
    selectionFollowsFocus,
    value,
    tabIndex,
    id,
    'aria-controls': ariaControls,
    ...other
  } = props;

  const handleClick: MouseEventHandler<HTMLElement> = (event) => {
    if (onChange) {
      onChange(event, value);
    }

    if (onClick) {
      onClick(event);
    }
  };

  const handleFocus: FocusEventHandler<HTMLElement> = (event) => {
    if (selectionFollowsFocus && !selected && onChange) {
      onChange(event, value);
    }

    if (onFocus) {
      onFocus(event);
    }
  };

  return (
    <ButtonBase
      className={cx(
        classes.root,
        styles.root,
        classes.textColorInherit,
        styles.textColorInherit,
        {
          [cx(classes.disabled, styles.disabled)]: disabled,
          [cx(classes.selected, styles.selected)]: selected,
          [cx(classes.labelIcon, styles.labelIcon)]: label && icon,
          [cx(classes.fullWidth, styles.fullWidth)]: fullWidth,
        },
        className,
        selected && styles.noHoverStyle,
      )}
      role="tab"
      id={id}
      aria-selected={selected}
      aria-controls={ariaControls}
      disabled={disabled}
      onClick={handleClick}
      onFocus={handleFocus}
      tabIndex={tabIndex}
      {...other}
    >
      <span className={cx(styles.wrapper, classes.tabWrapper)} data-testid="az-tab-component">
        {icon}
        {label}
      </span>
      {indicator}
    </ButtonBase>
  );
};

export default Tab;
